/**
 * Tooltip constructor for a procedure code's Connect financial breakdown.
 *
 * Pure + cheap, but intended to be called lazily (on hover) rather than during
 * change detection for every code. Returns '' when the code has no Connect
 * financial/status data, so callers can APPEND the result to an existing
 * tooltip (description, provider initials, etc.) without ever replacing it.
 */

import { formatRand } from './currency.util';

const SUBMISSION_STATUS_LABELS: Record<string, string> = {
  submitted_for_processing: 'Submitted to Connect',
  clinical_paid_up_front: 'Paid up front (clinical)',
};

function humanize(value: string): string {
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function toCentsOrNull(value: any): number | null {
  return value === null || value === undefined ? null : Number(value);
}

/**
 * Build the multi-line financial breakdown for a code, or '' if there is no
 * Connect financial/status information to show. Lines are newline-separated;
 * render with `white-space: pre-line` (see the `.financial-tooltip` class).
 */
export function buildCodeFinancialTooltip(code: any): string {
  if (!code) {
    return '';
  }

  const billed = toCentsOrNull(code.connectBilledCents);
  const approved = toCentsOrNull(code.connectApprovedCents);
  const rejected = toCentsOrNull(code.connectRejectedCents);
  const liable = toCentsOrNull(code.connectPatientLiableCents);
  const paid = toCentsOrNull(code.connectPaidCents);
  const clawback = toCentsOrNull(code.connectClawbackCents);

  const submissionStatus: string | undefined = code.connectSubmissionStatus;
  const claimStatus: string | undefined = code.connectClaimStatus;
  // MediKredit's verdict for THIS line, as opposed to the claim's.
  const lineStatus: string | undefined = code.connectLineStatus;
  // Billed at zero = a structural modifier marker (0005/0011/0008), which carries no
  // receivable and so can never be paid. Say so explicitly rather than showing a row of
  // R0.00 amounts that read as an unresolved line.
  const noCharge = billed !== null && billed === 0;
  // Connect deleted this line from the claim (claim.line_removed sets status 'removed').
  const removedByConnect = code.status === 'removed';

  const hasFinancial = [billed, approved, rejected, liable, paid, clawback].some((v) => v !== null);
  if (!removedByConnect && !hasFinancial && !submissionStatus && !claimStatus && !lineStatus) {
    return '';
  }

  const lines: string[] = [];

  // A Connect-removed line supersedes the submission status on the `Connect:` line.
  if (removedByConnect) {
    lines.push('Connect: Removed by Connect');
  } else if (submissionStatus) {
    lines.push(`Connect: ${SUBMISSION_STATUS_LABELS[submissionStatus] ?? humanize(submissionStatus)}`);
  }
  if (claimStatus) {
    lines.push(`Claim: ${humanize(claimStatus)}`);
  }
  // The line's own verdict, when the scheme gave one — this is what distinguishes a
  // modifier the scheme accepted from one it disallowed.
  if (lineStatus) {
    lines.push(`This line: ${humanize(lineStatus)}`);
  }

  if (noCharge) {
    lines.push('No charge — modifier on the linked procedure(s)');
    return lines.join('\n');
  }

  if (billed !== null) lines.push(`Billed: ${formatRand(billed)}`);
  if (approved !== null) lines.push(`Approved: ${formatRand(approved)}`);
  if (rejected !== null && rejected !== 0) lines.push(`Rejected: ${formatRand(rejected)}`);
  if (liable !== null && liable !== 0) lines.push(`Patient: ${formatRand(liable)}`);
  if (paid !== null) lines.push(`Paid: ${formatRand(paid)}`);
  if (clawback !== null && clawback !== 0) lines.push(`Clawback: ${formatRand(clawback)}`);

  return lines.join('\n');
}
